import { useEffect } from 'react';
import { identify, setBaseProperties, track } from '@adsk/acs-web-analytics';
import {
  useCurrentAccountId,
  useCurrentProductId,
  useProjectEntitlements,
  useUser,
  useSubscriptions,
} from '@adsk/alloy-provider';
import {
  SUBSCRIPTION_TYPES,
  SUBSCRIPTION_STATUSES,
} from '@adsk/acs-admin-ui-components-helpers';

/**
 The following event properties are set in the analytics lib:
  - user_type
  - project_type
  - screen_height
  - screen_width
  - project_id (from URL)
  - data_center
 The following event properties need to be set manually:
  - account_id
  - license
  - product_id
  - project_classification (not for Account Admin)
  - user_paying ?
 The Alloy ProjectPageLayout has a AnalyticsInitializer that sets the values for
  the previous event properties, this hook sets the properties when they are not initialized.
 */
const useAnalytics = () => {
  const currentAccountId = useCurrentAccountId();
  const currentProductId = useCurrentProductId();
  const { currentProject } = useProjectEntitlements();
  const { user } = useUser();
  const { data: subscriptions } = useSubscriptions();

  let licenseType = SUBSCRIPTION_TYPES.NONE;

  if (currentProductId && subscriptions && subscriptions.length > 0) {
    // get active subscription related to the current product
    const activeSubscriptions = subscriptions.filter(
      (subscription) =>
        subscription.products.some(
          (product) => product.productKey === currentProductId
        ) && subscription.status === SUBSCRIPTION_STATUSES.ACTIVE
    );

    if (activeSubscriptions.length === 1) {
      licenseType = activeSubscriptions[0].type;
    } else if (activeSubscriptions.length > 1) {
      // Determine if the current product has an active paid subscription
      const hasPaidSubscription = activeSubscriptions.some(
        (subscription) => subscription.type === SUBSCRIPTION_TYPES.PAID
      );

      licenseType = hasPaidSubscription
        ? SUBSCRIPTION_TYPES.PAID
        : SUBSCRIPTION_TYPES.TRIAL;
    }
  }

  useEffect(() => {
    if (currentAccountId && !window.accAnalyticsBaseProperties?.account_id) {
      setBaseProperties({
        account_id: currentAccountId,
      });
    }
  }, [currentAccountId]);

  useEffect(() => {
    if (currentProductId && !window.accAnalyticsBaseProperties?.product_id) {
      setBaseProperties({
        product_id: currentProductId,
        license: licenseType,
      });
    }
  }, [currentProductId, licenseType]);

  useEffect(() => {
    if (
      currentProject &&
      !window.accAnalyticsBaseProperties?.project_classification
    ) {
      setBaseProperties({
        project_classification: currentProject.classification,
      });
    }
  }, [currentProject]);

  useEffect(() => {
    if (user) identify(user.analyticsId, user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.analyticsId]);

  if (!user?.analyticsId) {
    return { trackEvent: () => {} };
  }

  return { trackEvent: track, ready: true };
};

export default useAnalytics;
